import React, { useState } from 'react';
import { API_ENDPOINTS } from '../../config';

const ApplicationForm = ({ data }) => {
  const [formData, setFormData] = useState({});
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const config = data || {
    sectionTitle: "Apply to Join Choice MCN",
    sectionDescription: "Start your journey with the world's most transparent Multi-Channel Network",
    fields: {},
    submitButton: "Submit Application"
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch(API_ENDPOINTS.applications.submit, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          howDidYouHear: 'WEBSITE'
        })
      });

      const result = await response.json();

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: config.successMessage?.replace('{referenceNumber}', result.referenceNumber || 'N/A') || 
                   `Application submitted successfully! Reference: ${result.referenceNumber || 'N/A'}`
        });
        setFormData({});
        e.target.reset();
      } else {
        setFormStatus({
          type: 'error',
          message: result.message || config.errorMessage || 'Failed to submit application'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: config.networkErrorMessage || 'Network error. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="apply-section" className="py-16 lg:py-24 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="text-3xl font-extrabold">{config.sectionTitle}</h2>
          <p className="mt-2 text-slate-600">{config.sectionDescription}</p>
        </div>
        
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {formStatus.message && (
              <div className={`p-4 rounded-lg mb-6 ${
                formStatus.type === 'success' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {formStatus.message}
              </div>
            )}
            
            <form onSubmit={handleFormSubmit}>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 text-sm">First Name *</label>
                  <input 
                    type="text" 
                    name="firstName" 
                    required 
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 text-sm">Last Name *</label>
                  <input 
                    type="text" 
                    name="lastName" 
                    required 
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 text-sm">Email Address *</label>
                  <input 
                    type="email" 
                    name="email" 
                    required 
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 text-sm">Phone Number *</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    pattern="[0-9]{10}" 
                    required 
                    onChange={handleInputChange}
                    placeholder="10 digits"
                    className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 text-sm">Content Niche *</label>
                  <select 
                    name="niche" 
                    required 
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                  >
                    <option value="">Select your niche</option>
                    <option value="EDUCATION">Education</option>
                    <option value="ENTERTAINMENT">Entertainment</option>
                    <option value="GAMING">Gaming</option>
                    <option value="TECH">Tech</option>
                    <option value="COOKING">Cooking</option>
                    <option value="TRAVEL">Travel</option>
                    <option value="FITNESS">Fitness</option>
                    <option value="FASHION">Fashion</option>
                    <option value="MUSIC">Music</option>
                    <option value="VLOG">Vlog</option>
                    <option value="BUSINESS">Business</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 text-sm">Upload Frequency</label>
                  <select 
                    name="videoFrequency" 
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                  >
                    <option value="WEEKLY">Weekly</option>
                    <option value="DAILY">Daily</option>
                    <option value="BIWEEKLY">Bi-weekly</option>
                    <option value="MONTHLY">Monthly</option>
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className="block mb-2 font-semibold text-slate-700 text-sm">YouTube Channel URL (if any)</label>
                <input 
                  type="url" 
                  name="channel" 
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/c/yourchannel"
                  className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 text-sm">Current Subscribers</label>
                  <input 
                    type="number" 
                    name="subscribers" 
                    min="0" 
                    onChange={handleInputChange}
                    placeholder="0"
                    className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 text-sm">Monthly Views</label>
                  <input 
                    type="number" 
                    name="monthlyViews" 
                    min="0" 
                    onChange={handleInputChange}
                    placeholder="0"
                    className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block mb-2 font-semibold text-slate-700 text-sm">Tell us about your content and goals *</label>
                <textarea 
                  name="about" 
                  required 
                  onChange={handleInputChange}
                  rows="4" 
                  placeholder="Describe your content style, experience, and what you hope to achieve with Choice MCN..."
                  className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none"
                />
              </div>

              <div className="mt-6 space-y-3">
                <label className="flex items-start gap-3">
                  <input type="checkbox" required className="mt-1" />
                  <span className="text-sm text-slate-600">I agree to the terms and conditions including revenue share as per the agreement *</span>
                </label>
                <label className="flex items-start gap-3">
                  <input type="checkbox" required className="mt-1" />
                  <span className="text-sm text-slate-600">I agree to follow YouTube content guidelines and MCN policies *</span>
                </label>
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="mt-6 w-full px-6 py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-blue-600 to-purple-600 shadow hover:shadow-lg transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationForm;